import React, {useState, useEffect, useRef} from "react";
import {resetUserEmail} from "../authRequests";

export default function ForgotPwdForm({org, onBack}) {
  const resetEmailRef = useRef(null);
  const [serverError, setServerError] = useState("");
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState("");
  const [sentMsg, setSentMsg] = useState("");

  function reset(e) {
    e.preventDefault();

    // No clinic, no reset. Sending without one is how a patient ends up asking
    // an organization they do not belong to for a password link.
    if (!org?.oid && !org?.db) {
      setServerError("Open your clinic's sign-in link first, then request the password link.");
      return;
    }

    setLoading(true);
    setAuthError(false);
    setServerError("");

    resetUserEmail(resetEmailRef.current.value, "patient", org)
      .then(() => {
        setLoading(false);
        setSentMsg(true);
      })
      .catch((err) => {
        setLoading(false);
        setSentMsg(false);

        const status = err.response?.status;
        const data = err.response?.data;

        if (status === 403) {
          setAuthError(true);
          return;
        }

        // The server explains these, and the explanation is the whole value.
        //
        //   404  the address is not registered with THIS clinic — and it names
        //        the clinic it looked in, because the usual cause is that the
        //        patient belongs to a different one and never noticed which was
        //        selected.
        //   409  the address is on file for several patients, so no single
        //        account can be reset from it. The clinic has to separate them,
        //        and saying "something went wrong" would send the patient round
        //        the same loop forever.
        //
        // Anything with a message gets that message; a generic apology in its
        // place throws away the only sentence that tells the patient what to do.
        if (typeof data === "object" && data?.message) {
          setServerError(data.message);
          return;
        }
        if (status === 404) {
          setServerError(
            `That email or phone number is not registered with ${
              org?.name || "this clinic"
            }. Check that you selected the right clinic.`
          );
          return;
        }
        setServerError("Something went wrong. Please try again.");
      });
  }

  return (
    <div className="w-full max-w-md">
      <form
        onSubmit={reset}
        className="w-full border border-gray-100 bg-white/80 backdrop-blur-md shadow-2xl rounded-2xl p-8 sm:p-10"
      >
        {org?.logo ? (
          <img
            src={org.logo}
            className="w-40 mx-auto mb-6"
            alt={`${org?.name || "Clinic"} logo`}
          />
        ) : (
          <img
            src="/images/logo.png"
            className="w-40 mx-auto mb-6"
            alt="Brightlight logo"
          />
        )}
        {serverError && (
          <div className="w-full flex justify-center">
            <div
              role="alert"
              className="mb-2 w-full rounded-xl border border-amber-200/70 bg-amber-50/80 px-4 py-2.5 text-amber-800 shadow-sm backdrop-blur"
            >
              <div className="flex items-start gap-3 text-left">
                <span className="mt-1 inline-block h-2 w-2 rounded-full bg-amber-400 ring-4 ring-amber-100"></span>
                <div className="flex-1">
                  <p className="font-semibold">We could not send the link</p>
                  {/* The real reason, not a generic apology. The usual one is
                      "you are not registered with the clinic you selected", and
                      the patient can only fix that if we tell them. */}
                  <p className="text-sm">{serverError}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-slate-800">
            Forgot password
          </h2>
          {/* NAME THE CLINIC. The reset is scoped to one organization, and a
              patient who cannot see which one has no way to notice they picked
              the wrong one — which is exactly how a link went out from a clinic
              the patient had never heard of. */}
          {org?.name ? (
            <p className="text-sm text-slate-500">
              We’ll send a secure link to reset your password at{" "}
              <span className="font-semibold text-slate-700">{org.name}</span>.
            </p>
          ) : (
            <p className="text-sm text-amber-700">
              Go back and choose your clinic first.
            </p>
          )}
        </div>
        {authError && (
          <div className="w-full flex justify-center">
            <div
              role="alert"
              className="mb-2 w-full rounded-xl border border-red-200/70 bg-red-50/80 px-4 py-2.5 text-red-700 shadow-sm backdrop-blur"
            >
              <div className="flex items-start gap-3 text-left">
                <span className="mt-1 inline-block h-2 w-2 rounded-full bg-red-400 ring-4 ring-red-100"></span>
                <div className="flex-1">
                  <p className="font-semibold">Request failed</p>
                  <p className="text-sm">Invalid email or phone.</p>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="input-group">
          <div className="input-label text-xs -ml-3 -mt-3">
            Please enter the email or phone registered with your Brightlight
            Account to access a secure link to change your password.
          </div>
          <input
            type="text"
            id="email"
            ref={resetEmailRef}
            required
            autoComplete="username"
            className="input mt-[4.5rem] w-full py-3 px-4 rounded-lg my-2 bg-white border border-gray-200 focus:border-[#6772e5] focus:ring-2 focus:ring-[#6772e5]/40 transition"
          />
          <label htmlFor="email" className="input-label mt-[4rem]">
            Email/Phone
          </label>
        </div>
        <div className="justify-center">
          <button
            type="submit"
            className="w-full p-2.5 px-5 rounded-lg my-2 flex justify-center items-center bg-[#6772e5] hover:bg-[#5b63d3] text-white font-medium text-sm cursor-pointer shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6772e5] transition-all duration-150"
          >
            Send reset link
            {loading && (
              <span className="">
                <svg
                  className="animate-spin ml-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="w-full text-xs text-slate-500 hover:text-slate-700 mt-2"
          >
            Back to login
          </button>
        </div>
        {sentMsg && (
          <div className="text-gray-500 mt-8 rounded-md">
            <div className="bg-light bg-opacity-25 p-4 rounded-md input-label text-xs -mt-3">
              You should have received an email or text with a verification link
              to reset your password.
              <br />
              <br />
              Please follow that link to continue or {""}
              <span
                className="text-[#6772e5] cursor-pointer hover:underline"
                onClick={reset}
              >
                resend
              </span>{" "}
              the link.
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
